import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, ExternalLink } from 'lucide-react';
import { truncateAddress, copyToClipboard } from '../lib/utils';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// Known addresses mapping
export const KNOWN_ADDRESSES = {
  // Pools
  '0x525911d60f3a767b5cf11ea1780d7a095d0075bf': { label: 'KROWN/USDT Pool', type: 'pool', color: 'text-emerald-500' },
  '0x3a4cbe34a07bd1a3b159edcccd9110c2a98d9a92': { label: 'KROWN/ETH Pool', type: 'pool', color: 'text-violet-500' },
  
  // DEX Contracts
  '0xe0ec010899d166559e7a22216659c8da16525c6d': { label: 'SwapRouter02', type: 'contract', color: 'text-blue-500' },
  '0xf8ea8a659f76789a29ba321c5c738b1c578e63cc': { label: 'QuoterV2', type: 'contract', color: 'text-blue-400' },
  '0x10ba7a9b45267caf016028cd398e68a19632bed2': { label: 'DEX Factory', type: 'contract', color: 'text-cyan-500' },
  
  // Tokens
  '0x9189d145d1d8e612c4c48bbd1af5a50cbee63d7d': { label: 'KROWN Token', type: 'token', color: 'text-amber-500' },
  '0x59973ae98d340ca0577ed92502402e82e987ba2e': { label: 'USDT Token', type: 'token', color: 'text-green-500' },
  '0xa3d9cfa4220d6a5ba7b9067d33ebedbd2de6f1cf': { label: 'USDC Token', type: 'token', color: 'text-green-400' },
  '0x6f9ec4f83ef69b923fa1dc00189e591728df0ac0': { label: 'ETH Token', type: 'token', color: 'text-indigo-500' },

  // Vesting
  '0xf28704c691290547924e2129d407da36bda8ce0f': { label: 'Krown Vesting', type: 'vesting', color: 'text-orange-400' },
};

// Helper to get known address info
export const getKnownAddressInfo = (address) => {
  if (!address) return null;
  return KNOWN_ADDRESSES[address.toLowerCase()] || null;
};

export function AddressBadge({ 
  address, 
  truncate = true, 
  link = true, 
  showCopy = true,
  showLabel = true,
  className = '' 
}) {
  const [copied, setCopied] = useState(false);
  const knownInfo = getKnownAddressInfo(address);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await copyToClipboard(address);
      setCopied(true);
      toast.success('Address copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const displayAddress = truncate ? truncateAddress(address) : address;
  
  // Show label if known address and showLabel is true
  const displayText = (showLabel && knownInfo) ? knownInfo.label : displayAddress;
  const textColorClass = (showLabel && knownInfo) ? knownInfo.color : '';

  const content = (
    <span className={`address-badge ${className}`}>
      <span className={`font-mono ${textColorClass}`}>
        {displayText}
      </span>
      {showCopy && (
        <button
          onClick={handleCopy}
          className="copy-btn p-0.5 rounded hover:bg-foreground/10"
          data-testid={`copy-address-${address?.slice(0, 8)}`}
          aria-label="Copy address"
        >
          {copied ? (
            <Check className="w-3 h-3 text-emerald-500" />
          ) : (
            <Copy className="w-3 h-3 text-muted-foreground" />
          )}
        </button>
      )}
    </span>
  );

  if (!link) return content;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={`/address/${address}`}
            className="inline-flex hover:opacity-80 transition-opacity"
            data-testid={`address-link-${address?.slice(0, 8)}`}
          >
            {content}
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">
            {knownInfo && <p className={`font-medium ${knownInfo.color}`}>{knownInfo.label}</p>}
            <p className="font-mono">{address}</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function TxHashBadge({ 
  hash, 
  truncate = true, 
  link = true, 
  showCopy = true,
  className = '' 
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await copyToClipboard(hash);
      setCopied(true);
      toast.success('Transaction hash copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const displayHash = truncate ? truncateAddress(hash, 10, 8) : hash;

  const content = (
    <span className={`address-badge ${className}`}>
      <span className="font-mono">{displayHash}</span>
      {showCopy && (
        <button
          onClick={handleCopy}
          className="copy-btn p-0.5 rounded hover:bg-foreground/10"
          data-testid={`copy-tx-${hash?.slice(0, 10)}`}
          aria-label="Copy transaction hash"
        >
          {copied ? (
            <Check className="w-3 h-3 text-emerald-500" />
          ) : (
            <Copy className="w-3 h-3 text-muted-foreground" />
          )}
        </button>
      )}
    </span>
  );

  if (!link) return content;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to={`/tx/${hash}`}
            className="inline-flex hover:opacity-80 transition-opacity"
            data-testid={`tx-link-${hash?.slice(0, 10)}`}
          >
            {content}
          </Link>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-mono text-xs">{hash}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
